export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const WEEKDAY_LABELS = {
  mon: 'Lundi',
  tue: 'Mardi',
  wed: 'Mercredi',
  thu: 'Jeudi',
  fri: 'Vendredi',
  sat: 'Samedi',
  sun: 'Dimanche',
};

function timeToMinutes(value) {
  const parts = String(value || '').trim().split(':');
  const h = Math.max(0, Math.min(23, Number(parts[0]) || 0));
  const m = Math.max(0, Math.min(59, Number(parts[1]) || 0));
  return h * 60 + m;
}

function normalizeTimeHHMM(value, fallback) {
  const v = String(value || '').trim();
  if (/^\d{1,2}:\d{2}$/.test(v)) {
    const [hh, mm] = v.split(':');
    return `${String(Number(hh) || 0).padStart(2, '0')}:${String(Number(mm) || 0).padStart(2, '0')}`;
  }
  return fallback;
}

export function getDayRanges(slot) {
  if (!slot) return [];
  const raw = Array.isArray(slot.ranges) ? slot.ranges : [];
  const parsed = raw
    .map((r) => ({
      from: normalizeTimeHHMM(r?.from, ''),
      to: normalizeTimeHHMM(r?.to, ''),
    }))
    .filter((r) => r.from && r.to && timeToMinutes(r.from) < timeToMinutes(r.to));
  if (parsed.length) return parsed.slice(0, 6);
  const from = normalizeTimeHHMM(slot.from, '12:00');
  const to = normalizeTimeHHMM(slot.to, '22:00');
  if (timeToMinutes(from) >= timeToMinutes(to)) return [];
  return [{ from, to }];
}

export function normalizeDaySlot(raw, fallback) {
  const fb = fallback || { open: false, from: '12:00', to: '22:00' };
  if (!raw || typeof raw !== 'object') {
    const ranges = getDayRanges(fb);
    return { ...fb, ranges };
  }
  const open = raw.open === true;
  const draft = {
    open,
    from: normalizeTimeHHMM(raw.from, fb.from),
    to: normalizeTimeHHMM(raw.to, fb.to),
    ranges: Array.isArray(raw.ranges)
      ? raw.ranges.map((r) => ({
          from: normalizeTimeHHMM(r?.from, fb.from),
          to: normalizeTimeHHMM(r?.to, fb.to),
        }))
      : undefined,
  };
  const ranges = getDayRanges(draft);
  const first = ranges[0];
  return {
    open,
    from: first?.from ?? draft.from,
    to: first?.to ?? draft.to,
    ranges,
  };
}

export function isDaySlotActive(slot) {
  if (!slot?.open) return false;
  return getDayRanges(slot).length > 0;
}

export function isTimeInDayRanges(timeHHMM, slot) {
  const t = timeToMinutes(normalizeTimeHHMM(timeHHMM, '00:00'));
  return getDayRanges(slot).some((r) => {
    const a = timeToMinutes(r.from);
    const b = timeToMinutes(r.to);
    return t >= a && t <= b;
  });
}

export function isInstantInDaySlot(slot, at) {
  if (!isDaySlotActive(slot)) return false;
  const d = at instanceof Date ? at : new Date(at);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return isTimeInDayRanges(`${hh}:${mm}`, slot);
}

export function filterSlotsByDayRanges(slots, slot) {
  const list = Array.isArray(slots) ? slots : [];
  if (!isDaySlotActive(slot)) return [];
  return list.filter((s) => isTimeInDayRanges(s, slot));
}

export function defaultPickupWeek() {
  const mk = (open, from, to) =>
    normalizeDaySlot({ open, from, to, ranges: [{ from, to }] }, { open, from, to });
  return {
    mon: mk(true, '11:00', '21:00'),
    tue: mk(true, '11:00', '21:00'),
    wed: mk(true, '11:00', '21:00'),
    thu: mk(true, '11:00', '21:00'),
    fri: mk(true, '11:00', '21:00'),
    sat: mk(true, '11:00', '21:00'),
    sun: mk(false, '11:00', '21:00'),
  };
}

/** Jours où l’on accepte les réservations (fermé par défaut jusqu’à configuration). */
export function defaultServiceWeek() {
  return WEEKDAY_KEYS.reduce((acc, key) => {
    acc[key] = normalizeDaySlot(
      { open: false, from: '12:00', to: '22:00', ranges: [{ from: '12:00', to: '22:00' }] },
      { open: false, from: '12:00', to: '22:00' },
    );
    return acc;
  }, {});
}

export function weekdayKeyFromDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const js = d.getDay();
  const map = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return map[js] || 'mon';
}

/** Sans horaires configurés en admin, aucun jour n’est réservable (calendrier grisé). */
export function isServiceDayOpen(serviceWeek, date) {
  if (!hasConfiguredServiceWeek(serviceWeek)) return false;
  const key = weekdayKeyFromDate(date);
  return isDaySlotActive(serviceWeek[key]);
}

export function buildReservationDayBookingsFromRequests(requests) {
  return (requests || [])
    .filter((r) => r.status === 'confirmed' || r.status === 'completed')
    .reduce((acc, r) => {
      const key = dayKeyFromIso(r.preferredDate);
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + (Number(r.partySize) || 0);
      return acc;
    }, {});
}

export function hasConfiguredServiceWeek(serviceWeek) {
  if (!serviceWeek || typeof serviceWeek !== 'object') return false;
  return WEEKDAY_KEYS.some((k) => isDaySlotActive(serviceWeek[k]));
}

export function hasConfiguredPickupWeek(pickupWeek) {
  if (!pickupWeek || typeof pickupWeek !== 'object') return false;
  return WEEKDAY_KEYS.some((k) => isDaySlotActive(pickupWeek[k]));
}

export const ORDER_STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  ready: 'Prête',
  completed: 'Terminée',
  cancelled: 'Annulée',
  fulfilled: 'Terminée',
};

export const ORDER_STATUS_STYLES = {
  pending: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  confirmed: 'bg-blue-500/15 text-blue-800 dark:text-blue-300',
  ready: 'bg-violet-500/15 text-violet-800 dark:text-violet-300',
  completed: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
  fulfilled: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
  cancelled: 'bg-muted text-muted-foreground',
};

export const RES_STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  completed: 'Terminée',
  cancelled: 'Annulée',
};

export function normalizeOrderStatus(status) {
  if (status === 'fulfilled') return 'completed';
  if (['pending', 'confirmed', 'ready', 'completed', 'cancelled'].includes(status)) {
    return status;
  }
  return 'pending';
}

export function orderStockDeducted(status) {
  const s = normalizeOrderStatus(status);
  return s === 'confirmed' || s === 'ready' || s === 'completed';
}

export function pendingUnitsForItem(orders, itemId) {
  return (orders || [])
    .filter((o) => normalizeOrderStatus(o.status) === 'pending')
    .reduce(
      (sum, o) =>
        sum +
        (o.lines || [])
          .filter((l) => l.itemId === itemId)
          .reduce((s, l) => s + (Number(l.quantity) || 0), 0),
      0,
    );
}

export function saleQuantityAvailable(item, orders) {
  const stock = Math.max(0, Number(item.quantityAvailable) || 0);
  const pending = pendingUnitsForItem(orders, item.id);
  return Math.max(0, stock - pending);
}

export function dayKeyFromDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function dayKeyFromIso(iso) {
  return dayKeyFromDate(iso);
}

export function confirmedCoversOnDay(requests, dayKey) {
  if (!dayKey) return 0;
  return (requests || [])
    .filter(
      (r) =>
        (r.status === 'confirmed' || r.status === 'completed') &&
        dayKeyFromIso(r.preferredDate) === dayKey,
    )
    .reduce((sum, r) => sum + (Number(r.partySize) || 0), 0);
}

export function formatOrderDate(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso || '—';
  }
}

export function formatReservationSlot(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso || '—';
  }
}
